import { useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CommentItem, CustomFieldDef, EntityType, FollowupItem } from '@shared/types';
import type { FilesResponse, HistoryResponse, MeetingItem, RecordKind, RecordSheet, TimelineItem, TimelinePage } from '@shared/record';

// Query keys sit under [type, id, ...] so the server's invalidation of [type, id] refreshes everything on a record.

export const keys = {
  record: (type: RecordKind, id: string) => [type, id] as const,
  sheet: (type: RecordKind, id: string) => [type, id, 'sheet'] as const,
  timeline: (type: RecordKind, id: string) => [type, id, 'timeline'] as const,
  files: (type: RecordKind, id: string) => [type, id, 'files'] as const,
  history: (type: RecordKind, id: string) => [type, id, 'history'] as const,
  meetings: (type: RecordKind, id: string) => [type, id, 'meetings'] as const,
  comments: (type: EntityType, id: string) => [type, id, 'comments'] as const,
  // Under ['followups'] so that any follow-up change anywhere refreshes the box.
  followups: (type: RecordKind, id: string) => ['followups', 'record', type, id] as const,
  fields: (entity: 'people' | 'companies') => ['fields', entity] as const,
  tags: ['tags'] as const,
};

export function entityOf(type: RecordKind): 'people' | 'companies' {
  return type === 'person' ? 'people' : 'companies';
}

export function useSheet(type: RecordKind, id: string) {
  return useQuery({
    queryKey: keys.sheet(type, id),
    queryFn: ({ signal }) => api.get<RecordSheet>(`/record/${type}/${id}`, { signal }),
    retry: (count, err) => (err as { status?: number }).status !== 404 && count < 2,
  });
}

export function useTimeline(type: RecordKind, id: string) {
  return useInfiniteQuery({
    queryKey: keys.timeline(type, id),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<TimelinePage>(`/record/${type}/${id}/timeline`, { signal, query: { before: pageParam, limit: 40 } }),
    getNextPageParam: (last) => last.nextBefore,
  });
}

export function useMeetings(type: RecordKind, id: string) {
  return useQuery({
    queryKey: keys.meetings(type, id),
    queryFn: ({ signal }) => api.get<{ upcoming: MeetingItem[]; past: MeetingItem[] }>(`/record/${type}/${id}/meetings`, { signal }),
  });
}

export function useFiles(type: RecordKind, id: string) {
  return useQuery({
    queryKey: keys.files(type, id),
    queryFn: ({ signal }) => api.get<FilesResponse>('/files', { signal, query: { entityType: type, entityId: id } }),
  });
}

export function useHistory(type: RecordKind, id: string) {
  return useQuery({
    queryKey: keys.history(type, id),
    queryFn: ({ signal }) => api.get<HistoryResponse>('/history', { signal, query: { entityType: type, entityId: id } }),
  });
}

export function useRecordFollowups(type: RecordKind, id: string) {
  return useQuery({
    queryKey: keys.followups(type, id),
    queryFn: ({ signal }) =>
      api.get<FollowupItem[]>('/followups', {
        signal,
        query: { [type === 'person' ? 'personId' : 'companyId']: id, related: true, status: 'open' },
      }),
  });
}

export function useComments(entityType: EntityType, entityId: string) {
  return useQuery({
    queryKey: keys.comments(entityType, entityId),
    queryFn: ({ signal }) => api.get<CommentItem[]>('/comments', { signal, query: { entityType, entityId, order: 'asc' } }),
  });
}

export function useFields(type: RecordKind) {
  return useQuery({
    queryKey: keys.fields(entityOf(type)),
    queryFn: ({ signal }) => api.get<CustomFieldDef[]>('/fields', { signal, query: { entity: entityOf(type) } }),
    staleTime: 60_000,
  });
}

export function useTags(enabled: boolean) {
  return useQuery({
    queryKey: keys.tags,
    queryFn: ({ signal }) => api.get<{ tag: string; people: number; companies: number; deals: number }[]>('/tags', { signal }),
    enabled,
    staleTime: 30_000,
  });
}

/** Applies a change to the cached sheet straight away. Returns a function that puts the old sheet back. */
export function patchSheetCache(qc: QueryClient, type: RecordKind, id: string, change: (s: RecordSheet) => RecordSheet): () => void {
  const key = keys.sheet(type, id);
  const before = qc.getQueryData<RecordSheet>(key);
  if (before) qc.setQueryData<RecordSheet>(key, change(before));
  return () => {
    if (before) qc.setQueryData<RecordSheet>(key, before);
  };
}

/** Puts a freshly written item at the top of the cached timeline, ahead of the server's refresh. */
export function prependTimeline(qc: QueryClient, type: RecordKind, id: string, item: TimelineItem): void {
  qc.setQueryData<{ pages: TimelinePage[]; pageParams: unknown[] }>(keys.timeline(type, id), (data) => {
    if (!data || !data.pages.length) return data;
    const [first, ...rest] = data.pages;
    if (first!.items.some((x) => x.id === item.id)) return data;
    return { ...data, pages: [{ ...first!, items: [item, ...first!.items] }, ...rest] };
  });
}

/** Removes an item from the cached timeline, for a delete or an undone post. */
export function dropTimeline(qc: QueryClient, type: RecordKind, id: string, itemId: string): void {
  qc.setQueryData<{ pages: TimelinePage[]; pageParams: unknown[] }>(keys.timeline(type, id), (data) =>
    data ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.id !== itemId) })) } : data,
  );
}

export function useRecordInvalidate(type: RecordKind, id: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.record(type, id) });
}
